import { X } from "lucide-react"
import { useId, useState } from "react"
import { useTranslation } from "react-i18next"

import { cn, formatAmount, formatDateExtended, formatDateShort } from "@/lib/utils"
import { invoicesTotal, payableInvoices, rentInvoicePeriod } from "@/lib/invoice-utils"
import { isLegalAddress, unitTypeLabel } from "@/lib/contract-utils"
import { Button as UiKitButton } from "@/components/ui-kit/button"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui-kit/card"
import { ContractServiceChips } from "@/components/contracts/contracts-service-chips"
import { ContractsPaymentBadge } from "@/components/contracts/contracts-payment-badge"
import { ContractsPayDialog } from "@/components/contracts/modals/contracts-pay-dialog"
import { ContractsRecurrentDisableDialog } from "@/components/contracts/modals/contracts-recurrent-disable-dialog"
import { ContractsRentEndDialog } from "@/components/contracts/modals/contracts-rent-end-dialog"
import { BoxIcon, LockIcon, CreditCardIcon } from "@/components/icons"
import type { Contract } from "@/types/contract"
import type { Invoice } from "@/types/invoice"
import { ContractsDocumentsMenu } from "@/components/contracts/contracts-documents-menu"
import { ContractsInvoicesDialog } from "@/components/contracts/modals/contracts-invoices-dialog"
export type ContractsCardProps = {
  contract: Contract
  props: {
    isGroupLocked: boolean
  }
}

export function ContractsCard({
  contract,
  props,
}: ContractsCardProps) {
  const { t } = useTranslation()
  const titleId = useId()
  const [isPayDialogOpen, setIsPayDialogOpen] = useState(false)
  const [invoicesToPay, setInvoicesToPay] = useState<Invoice[]>([])
  const [isRentEndDialogOpen, setIsRentEndDialogOpen] = useState(false)
  const [isInvoicesDialogOpen, setIsInvoicesDialogOpen] = useState(false)
  const [isRecurrentDisableDialogOpen, setIsRecurrentDisableDialogOpen] = useState(false)
  const isLocked = contract.status === "locked" || props.isGroupLocked
  const canEndRent = contract.status === "active"
  const legalAddress = isLegalAddress(contract)
  const typeLabel = legalAddress ? null : unitTypeLabel(contract)

  const unpaidInvoices = payableInvoices(contract.invoices ?? [])
  const unpaidInvoicesSum = invoicesTotal(unpaidInvoices)
  const invoicePeriod = contract.status === "closed" ? null : rentInvoicePeriod(contract.invoices ?? [])
  const handlePayInvoices = (invoices: Invoice[]) => {
    setIsInvoicesDialogOpen(false)
    setInvoicesToPay(invoices)
    requestAnimationFrame(() => setIsPayDialogOpen(true))
  }

  const handlePayClick = () => {
    if (unpaidInvoices.length === 1) {
      setInvoicesToPay(unpaidInvoices)
      setIsPayDialogOpen(true)
      return
    }

    setIsInvoicesDialogOpen(true)
  }

  return (
    <Card aria-labelledby={titleId} className="p-4 sm:p-6">
      <ContractsDocumentsMenu contract={contract} />

      <div className="flex items-center gap-4 sm:mb-8 mb-6">
        <div
          className={cn(
            "flex size-14 shrink-0 items-center justify-center rounded-full bg-input",
            isLocked && "bg-[#fff3f3] text-[#df0f0f]",
          )}
        >
          {isLocked ? <LockIcon size={32} className="size-[32px]" /> : <BoxIcon />}
        </div>

        <div className="flex flex-wrap items-baseline gap-x-3">
          <h2 id={titleId} className="sm:text-[28px] text-[24px] sm:leading-[34px] leading-[30px] font-semibold">
            {contract.unitItem?.code ?? contract.unit}
          </h2>
          {legalAddress ? (
            <span className="sm:text-lg text-[16px] sm:leading-6 leading-[22px] text-muted-foreground">
              {t("contracts.legalAddress")}
            </span>
          ) : null}
        </div>
      </div>

      <div className="sm:mb-6 mb-4">
        <div className="flex flex-wrap items-center sm:gap-4 gap-2">
          {typeLabel ? (
            <span className="rounded-full bg-input px-3 py-1 sm:text-lg text-[16px] sm:leading-6 leading-[22px]">
              {typeLabel}
            </span>
          ) : null}
          <ContractsPaymentBadge variant={contract.status} paidUntil={contract.paidUntil} />
        </div>
        {invoicePeriod ? (
          <div className="mt-2 sm:text-lg text-[16px] sm:leading-6 leading-[22px] text-muted-foreground">
            {t("contracts.invoicePeriod", {
              from: formatDateShort(invoicePeriod.from),
              to: formatDateShort(invoicePeriod.to),
            })}
          </div>
        ) : null}
      </div>

      <ContractServiceChips lineDetails={contract.lineDetails} primaryLineId={contract.primaryLineId} />

      {contract.status === "active" && contract.isRecurrent && contract.paidUntil ? (
        <div className="sm:mb-8 mb-6 flex items-center gap-2">
          <div className="text-lg leading-6">
            {t("contracts.recurrent.description", { paidUntil: formatDateExtended(contract.paidUntil) })}
          </div>

          <Button
            variant="link"
            className="inline cursor-pointer text-lg leading-6 text-brand hover:underline"
            onClick={() => setIsRecurrentDisableDialogOpen(true)}
          >
            Отключить
          </Button>
        </div>
      ) : null}

      {(contract.status === "pending" || contract.status === "pending_manual") && contract.endedAt ? (
        <div className="mb-8 flex items-center gap-2">
          <div className="text-lg leading-6">
            {t("contracts.pending.description", { endedAt: formatDateExtended(contract.endedAt) })}
          </div>
        </div>
      ) : null}

      <div className="flex flex-wrap sm:gap-4 gap-2">
        {unpaidInvoices.length > 0 && (
          <UiKitButton
            type="button"
            className="min-w-0 grow basis-[200px] sm:text-lg text-[16px] leading-6 h-[48px] sm:h-auto"
            endIcon={<CreditCardIcon size={24} />}
            onClick={handlePayClick}
          >
            Оплатить {formatAmount(unpaidInvoicesSum)}
          </UiKitButton>
        )}

        {canEndRent ? (
          <UiKitButton
            variant="secondary"
            type="button"
            className="min-w-0 grow basis-[200px] sm:text-lg text-[16px] leading-6 h-[48px] sm:h-auto"
            endIcon={<X data-icon="inline-end" aria-hidden size={24} />}
            onClick={() => setIsRentEndDialogOpen(true)}
          >
            Завершить аренду
          </UiKitButton>
        ) : null}
      </div>

      <ContractsInvoicesDialog
        open={isInvoicesDialogOpen}
        onOpenChange={setIsInvoicesDialogOpen}
        contract={contract}
        onPay={handlePayInvoices}
      />
      <ContractsPayDialog
        open={isPayDialogOpen}
        onOpenChange={(open) => {
          setIsPayDialogOpen(open)
          if (!open) {
            setInvoicesToPay([])
          }
        }}
        invoices={invoicesToPay}
        contractId={contract.id}
        isRecurrent={contract.isRecurrent}
      />
      <ContractsRentEndDialog
        open={isRentEndDialogOpen}
        onOpenChange={setIsRentEndDialogOpen}
        contract={contract}
      />
      <ContractsRecurrentDisableDialog
        open={isRecurrentDisableDialogOpen}
        onOpenChange={setIsRecurrentDisableDialogOpen}
        contract={contract}
      />
    </Card>
  )
}
